// os#231: which tool connections a plan needs that the enterprise has not made yet.
//
// A plan can name a perfectly sensible task whose tools nobody has connected. Nothing used to check
// that until a run was already starting, so the CEO approved, the task was built, and the first run
// failed on a missing connection. These helpers let a plan be read against the live list of connected
// tool types, so the gap is named *before* sign-off. They only warn: approving is still allowed.
import { toolEntryNames } from '@zerohuman/shared'
import type { PlanItem } from '@/api'

/** The type of a tool id: the segment before the first `.` (`slack` from `slack.post_message`). */
export function toolTypeOf(tool: string): string {
    return tool.split('.')[0] ?? tool
}

/**
 * Which types among `tools` have no entry in `connectedTypes` (the tool types with a connection).
 * Each missing type is named once, in the order the plan first asks for it, however many of its
 * tools it lists. Mirrors the API's `missingToolConnections` (`apps/api/src/worker/improve.ts`).
 */
export function missingToolConnections(tools: string[], connectedTypes: string[]): string[] {
    const connected = new Set(connectedTypes.map(toolTypeOf))
    const missing: string[] = []
    for (const tool of tools) {
        const type = toolTypeOf(tool)
        if (type === '' || connected.has(type) || missing.includes(type)) {
            continue
        }
        missing.push(type)
    }
    return missing
}

/**
 * Every tool an `os_change` plan's items ask for. A `delete_task` item is skipped: a task on its way
 * out needs no connection. Each entry is read by the tool name it holds, a string or an object naming
 * it (os#1687); an entry that names none is ignored rather than guessed at, so a malformed item never
 * invents a warning.
 */
export function planProposedTools(items: PlanItem[]): string[] {
    const tools: string[] = []
    for (const item of items) {
        if (typeof item.type === 'string' && item.type.startsWith('delete_')) {
            continue
        }
        const value = (item as Record<string, unknown>).tools
        if (!Array.isArray(value)) {
            continue
        }
        for (const tool of toolEntryNames(value)) {
            if (!tools.includes(tool)) {
                tools.push(tool)
            }
        }
    }
    return tools
}
